import React, { useState, useEffect } from 'react';
import { Award } from 'lucide-react';
import './References.css';
import useSEO from '../hooks/useSEO';

const References = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useSEO({
    title: 'Referanslarımız - Efruz İnşaat | İş Ortaklarımız ve Müşteri Görüşleri',
    description: 'Efruz İnşaat olarak Türkiye\'nin önde gelen kurumlarıyla çalışıyoruz. Koç Holding, Sabancı Holding, Zorlu Holding gibi prestijli referanslarımız ve müşteri görüşlerimizi inceleyin.',
    keywords: 'efruz inşaat referansları, iş ortakları, müşteri görüşleri, kurumsal müşteriler, inşaat referansları, başarı hikayeleri'
  });

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const response = await fetch(`${API_BASE}/references.php`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        console.log('References API Response:', text.substring(0, 200));
        const result = JSON.parse(text);
        if (result.success) {
          setClients(result.data || []);
        } else {
          console.error('Failed to fetch references:', result.error);
          setClients([]);
        }
      } catch (error) {
        console.error('Error fetching references:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, []);


  const awards = [
    {
      id: 1,
      title: 'Yılın İnşaat Firması',
      organization: 'Türkiye İnşaat Sanayicileri İşveren Sendikası',
      year: '2023'
    },
    {
      id: 2,
      title: 'En İyi Sürdürülebilir Proje Ödülü',
      organization: 'Green Building Council',
      year: '2023'
    },
    {
      id: 3,
      title: 'Mükemmellik Ödülü',
      organization: 'İstanbul Ticaret Odası',
      year: '2022'
    },
    {
      id: 4,
      title: 'Kalite Yönetim Sertifikası',
      organization: 'ISO 9001:2015',
      year: '2021'
    }
  ];

  const filteredClients = activeCategory === 'all' 
    ? clients 
    : clients.filter(client => client.category === activeCategory);

  return (
    <div className="references-page">
      {/* Page Title Section */}
      <section className="page-title-section">
        <div className="container">
          <h1 className="page-title">Referanslarımız</h1>
          <p className="page-description">
            Türkiye'nin önde gelen kurumlarıyla çalışıyoruz. Koç Holding, Sabancı Holding, Zorlu Holding gibi prestijli referanslarımız, kalitemizin ve güvenilirliğimizin en büyük kanıtıdır.
          </p>
        </div>
      </section>

      {/* Clients Logos */}
      <section className="clients-section" style={{ paddingTop: '60px', paddingBottom: '140px' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">İş Ortaklarımız</h2>
            <p className="body-large">Türkiye'nin önde gelen kurumlarıyla çalışıyoruz</p>
          </div>

          {/* Category Filter */}
          <div className="client-filter">
            <button 
              className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              Tümü
            </button>
            <button 
              className={`filter-btn ${activeCategory === 'corporate' ? 'active' : ''}`}
              onClick={() => setActiveCategory('corporate')}
            >
              Kurumsal
            </button>
            <button 
              className={`filter-btn ${activeCategory === 'government' ? 'active' : ''}`}
              onClick={() => setActiveCategory('government')}
            >
              Kamu
            </button>
          </div>

          {/* Clients Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p>Yükleniyor...</p>
            </div>
          ) : filteredClients.length > 0 ? (
            <div className="clients-grid">
              {filteredClients.map((client) => (
                <div key={client.id} className="client-card hover-lift">
                  <div className="client-logo">
                    <img src={client.logo} alt={client.name} />
                  </div>
                  <div className="client-info">
                    <h4 className="body-small">{client.name}</h4>
                    {client.project && (
                      <p className="client-project">{client.project}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p>Henüz referans eklenmemiş.</p>
            </div>
          )}
        </div>
      </section>

      {/* Awards & Certifications */}
      <section className="awards-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Ödüller ve Sertifikalar</h2>
            <p className="body-large">Kalitemizin tescili</p>
          </div>

          <div className="awards-grid">
            {awards.map((award) => (
              <div key={award.id} className="award-card hover-lift">
                <div className="award-icon">
                  <Award size={32} />
                </div>
                <h3 className="heading-3">{award.title}</h3>
                <p className="body-regular">{award.organization}</p>
                <span className="award-year">{award.year}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="refs-cta-section section-padding-small">
        <div className="container">
          <div className="refs-cta-box">
            <h2 className="heading-2">Bir sonraki referansımız siz olun</h2>
            <p className="body-large">Projeleriniz için güvenilir çözüm ortağınız olmaya hazırız</p>
            <a href="/contact" className="btn-primary">
              İletişime Geçin
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default References;